import React, { Children } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { motion } from 'framer-motion';
import { Music, Utensils, GlassWater, Mic2, Clock, Calendar, Star, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Brunch() {
  const features = [{
    icon: Mic2,
    title: 'Karaoke Style',
    description: 'Sing your heart out with our live karaoke experience during brunch.'
  }, {
    icon: Utensils,
    title: 'Bottomless Food',
    description: 'Enjoy an endless array of gourmet brunch favorites prepared fresh.'
  }, {
    icon: GlassWater,
    title: 'Bottomless Mimosas',
    description: 'Keep the celebration flowing with our signature bottomless mimosas.'
  }];
  const menuItems = [{
    name: 'Signature Chicken & Waffles',
    price: '$22',
    description: 'Crispy southern fried chicken over Belgian waffles with spicy maple syrup.'
  }, {
    name: 'Lobster Benedict',
    price: '$28',
    description: 'Fresh Atlantic lobster, poached eggs, and hollandaise on toasted brioche.'
  }, {
    name: 'Stuffed French Toast',
    price: '$18',
    description: 'Brioche bread stuffed with sweetened cream cheese and fresh berries.'
  }, {
    name: 'Shrimp & Grits',
    price: '$24',
    description: 'Jumbo shrimp, stone-ground grits, and a savory andouille sausage gravy.'
  }];
  return <div className="min-h-screen bg-[#0B1014] text-white selection:bg-[#DDA247] selection:text-[#0B1014]" data-id="element-952">
      <Header data-id="element-953" />

      <main className="pt-24 md:pt-44 pb-16" data-id="element-954">
        {/* Hero Section */}
        <section className="container mx-auto px-6 mb-16" data-id="element-955">
          <div className="relative h-[500px] md:h-[600px] rounded-3xl overflow-hidden" data-id="element-956">
            <img src="https://cdn.magicpatterns.com/uploads/6VYL7C1UR5Mf2BFZpi16Fi/SnapInsta.to_683810361_18095297495328170_5024856363855259451_n.jpg" alt="Melodies & Mimosas Brunch" className="absolute inset-0 w-full h-full object-cover" data-id="element-957" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1014] via-[#0B1014]/40 to-transparent" data-id="element-958" />

            <div className="absolute inset-0 flex flex-col justify-center p-8 md:p-16" data-id="element-959">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-960">
                <div className="flex flex-wrap items-center gap-2 mb-6" data-id="element-961">
                  <span className="px-4 py-1 bg-[#DDA247] text-[#0B1014] font-black text-xs uppercase tracking-widest" data-id="element-962">
                    Sundays • 11AM - 4PM
                  </span>
                  <span className="px-4 py-1 bg-white/10 text-white font-black text-xs uppercase tracking-widest backdrop-blur-md" data-id="element-963">
                    Karaoke Experience
                  </span>
                </div>
                <h1 className="text-4xl md:text-6xl font-black text-white mb-6 uppercase tracking-tight leading-none" data-id="element-964">
                  Melodies & <br data-id="element-965" />
                  <span className="text-[#DDA247]" data-id="element-966">Mimosas</span>
                </h1>
                <p className="text-lg md:text-xl text-gray-200 max-w-2xl mb-10 leading-relaxed" data-id="element-967">
                  The ultimate karaoke-style brunch experience. Bottomless food,
                  bottomless mimosas, and a party atmosphere that sets the tone
                  for your Sunday.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-968">
                  <Link to="/book-now" data-id="element-969">
                    <Button size="lg" className="min-w-[220px]" data-id="element-970">
                      Reserve Brunch Spot
                    </Button>
                  </Link>
                  <Button variant="outline" size="lg" data-id="element-971">
                    View Brunch Menu
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="container mx-auto px-6 mb-16" data-id="element-972">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={{
          hidden: {
            opacity: 0
          },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.1
            }
          }
        }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-973">
            {features.map(feature => <motion.div key={feature.title} variants={{
            hidden: {
              opacity: 0,
              y: 30
            },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6
              }
            }
          }} className="bg-[#11161A] border border-white/5 p-10 rounded-2xl text-center group hover:border-[#DDA247]/30 transition-colors" data-id="element-974">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#DDA247]/10 text-[#DDA247] mb-6 group-hover:bg-[#DDA247] group-hover:text-[#0B1014] transition-colors" data-id="element-975">
                  <feature.icon size={32} data-id="element-976" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 uppercase tracking-tight" data-id="element-977">
                  {feature.title}
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-978">
                  {feature.description}
                </p>
              </motion.div>)}
          </motion.div>
        </section>

        {/* Menu Highlights */}
        <section className="container mx-auto px-6 mb-16" data-id="element-979">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="bg-[#11161A] border border-white/5 rounded-3xl overflow-hidden" data-id="element-980">
            <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-981">
              <div className="relative min-h-[400px]" data-id="element-982">
                <img src="https://cdn.magicpatterns.com/uploads/tpWemGKh6PJ8UBF26GyTiu/SnapInsta.to_683411563_18095297477328170_5374460014215317774_n.jpg" alt="Gourmet Brunch Plate" className="absolute inset-0 w-full h-full object-cover" data-id="element-983" />
                <div className="absolute inset-0 bg-gradient-to-l from-[#11161A] to-transparent lg:block hidden" data-id="element-984" />
              </div>
              <div className="p-8 md:p-16" data-id="element-985">
                <h2 className="text-4xl font-bold mb-8 uppercase tracking-tight" data-id="element-986">
                  Menu <span className="text-[#DDA247]" data-id="element-987">Highlights</span>
                </h2>
                <div className="space-y-8" data-id="element-988">
                  {menuItems.map((item, i) => <motion.div key={item.name} initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: i * 0.1
                }} className="flex justify-between items-start border-b border-white/5 pb-6 last:border-0" data-id="element-989">
                      <div className="max-w-[80%]" data-id="element-990">
                        <h4 className="text-white font-bold uppercase tracking-wide mb-1" data-id="element-991">
                          {item.name}
                        </h4>
                        <p className="text-gray-500 text-sm" data-id="element-992">
                          {item.description}
                        </p>
                      </div>
                      <span className="text-[#DDA247] font-bold" data-id="element-993">
                        {item.price}
                      </span>
                    </motion.div>)}
                </div>
                <div className="mt-10" data-id="element-994">
                  <Button variant="outline" fullWidth data-id="element-995">
                    Download Full Brunch Menu
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Experience Details */}
        <section className="container mx-auto px-6" data-id="element-996">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-997">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-998">
              <h2 className="text-4xl font-bold text-white mb-8 uppercase tracking-tight" data-id="element-999">
                The Sunday <span className="text-[#DDA247]" data-id="element-1000">Vibe</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-10" data-id="element-1001">
                Melodies & Mimosas isn't just a meal—it's a full-scale
                production. Our live DJ sets the mood while our host leads the
                crowd in an unforgettable karaoke experience. Whether you're
                celebrating a birthday or just the weekend, this is where you
                want to be.
              </p>
              <div className="space-y-6" data-id="element-1002">
                <div className="flex items-start gap-4" data-id="element-1003">
                  <div className="w-12 h-12 rounded-xl bg-[#DDA247]/10 flex items-center justify-center text-[#DDA247] shrink-0" data-id="element-1004">
                    <Clock size={24} data-id="element-1005" />
                  </div>
                  <div data-id="element-1006">
                    <h4 className="text-white font-bold uppercase text-sm tracking-widest mb-1" data-id="element-1007">
                      Hours
                    </h4>
                    <p className="text-gray-400" data-id="element-1008">
                      Every Sunday: 11:00 AM - 4:00 PM
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1009">
                  <div className="w-12 h-12 rounded-xl bg-[#DDA247]/10 flex items-center justify-center text-[#DDA247] shrink-0" data-id="element-1010">
                    <Calendar size={24} data-id="element-1011" />
                  </div>
                  <div data-id="element-1012">
                    <h4 className="text-white font-bold uppercase text-sm tracking-widest mb-1" data-id="element-1013">
                      Rum Punch Sundays
                    </h4>
                    <p className="text-gray-400" data-id="element-1014">
                      Followed by our evening event starting at 5:00 PM.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1015">
                  <div className="w-12 h-12 rounded-xl bg-[#DDA247]/10 flex items-center justify-center text-[#DDA247] shrink-0" data-id="element-1016">
                    <Star size={24} data-id="element-1017" />
                  </div>
                  <div data-id="element-1018">
                    <h4 className="text-white font-bold uppercase text-sm tracking-widest mb-1" data-id="element-1019">
                      Pricing
                    </h4>
                    <p className="text-gray-400" data-id="element-1020">
                      Starting at $45 per person for bottomless options.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-12" data-id="element-1021">
                <Link to="/book-now" data-id="element-1022">
                  <Button size="lg" className="min-w-[200px]" data-id="element-1023">
                    Book Your Table
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative aspect-square rounded-3xl overflow-hidden" data-id="element-1024">
              <img src="https://images.unsplash.com/photo-1519046904884-53103b34b206?q=80&w=1000&auto=format&fit=crop" alt="Brunch Party Atmosphere" className="w-full h-full object-cover" data-id="element-1025" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1014]/60 to-transparent" data-id="element-1026" />
              <div className="absolute bottom-8 left-8 right-8 text-center bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10" data-id="element-1027">
                <Heart className="text-[#DDA247] mx-auto mb-3" fill="#DDA247" size={24} data-id="element-1028" />
                <p className="text-white font-bold uppercase tracking-widest text-sm" data-id="element-1029">
                  Voted Best Brunch in Laurel
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1030" />
    </div>;
}